/**
 * Pathfinding.
 *
 * Routes are found on the tile grid and walked from one tile centre to the next, so
 * every leg runs along one of the eight compass directions. That is what keeps a
 * walk looking deliberate rather than drifting at whatever angle a shortcut happened
 * to produce.
 *
 * The search weighs a diagonal step at its true length rather than counting steps,
 * because an entity here moves at a constant speed along the line rather than one
 * tile per tick. Counting steps finds the route with fewest tiles, which is not the
 * same as the one that takes least time, and the difference shows up as a walk that
 * wanders on its way somewhere obvious.
 *
 * When a destination cannot be reached the search returns a route to the closest
 * reachable tile instead of failing, so clicking across a river still walks to the bank.
 */

import { CollisionMap } from './collision';
import { centerOf, DIRECTIONS, type TilePos, type WorldPos } from './tiles';

/** Widest area the search will explore, in tiles, centred on the start. */
const SEARCH_SIZE = 128;
const SEARCH_HALF = SEARCH_SIZE / 2;

export interface PathResult {
  /** Tile route, excluding the starting tile. Empty when already at the destination. */
  tiles: TilePos[];
  /** The same route as world positions: one tile centre per step. */
  points: WorldPos[];
  /** False when the exact destination was unreachable and a nearby tile was used. */
  exact: boolean;
}

const EMPTY_PATH: PathResult = { tiles: [], points: [], exact: true };

export function findPath(map: CollisionMap, start: TilePos, goal: TilePos): PathResult {
  if (start.plane !== goal.plane) return EMPTY_PATH;
  if (start.tx === goal.tx && start.tz === goal.tz) return EMPTY_PATH;

  const plane = start.plane;
  const originX = start.tx - SEARCH_HALF;
  const originZ = start.tz - SEARCH_HALF;

  const indexOf = (tx: number, tz: number): number => {
    const lx = tx - originX;
    const lz = tz - originZ;
    if (lx < 0 || lz < 0 || lx >= SEARCH_SIZE || lz >= SEARCH_SIZE) return -1;
    return lz * SEARCH_SIZE + lx;
  };

  const startIndex = indexOf(start.tx, start.tz);
  if (startIndex < 0) return EMPTY_PATH;
  const goalIndex = indexOf(goal.tx, goal.tz);

  const cameFrom = new Int32Array(SEARCH_SIZE * SEARCH_SIZE).fill(-1);
  const cost = new Float32Array(SEARCH_SIZE * SEARCH_SIZE).fill(Infinity);
  const closed = new Uint8Array(SEARCH_SIZE * SEARCH_SIZE);

  const open = new Heap(SEARCH_SIZE * SEARCH_SIZE);
  cost[startIndex] = 0;
  open.push(startIndex, octile(start.tx, start.tz, goal.tx, goal.tz));

  // The closest the search ever came, for a destination that turns out to be
  // unreachable: clicking the far bank of a river should walk to the near one.
  let bestIndex = startIndex;
  let bestDistance = octile(start.tx, start.tz, goal.tx, goal.tz);
  let found = false;

  while (!open.empty) {
    const current = open.pop();
    if (current < 0) break;
    if (closed[current]) continue;
    closed[current] = 1;

    if (current === goalIndex) {
      found = true;
      bestIndex = current;
      break;
    }

    const cx = originX + (current % SEARCH_SIZE);
    const cz = originZ + Math.floor(current / SEARCH_SIZE);

    const distance = octile(cx, cz, goal.tx, goal.tz);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = current;
    }

    const here = cost[current] ?? Infinity;

    for (const dir of DIRECTIONS) {
      const nx = cx + dir.dx;
      const nz = cz + dir.dz;
      const next = indexOf(nx, nz);
      if (next < 0 || closed[next]) continue;
      if (!map.canStep(cx, cz, nx, nz, plane)) continue;

      const step = dir.dx !== 0 && dir.dz !== 0 ? DIAGONAL : 1;
      const through = here + step;
      if (through >= (cost[next] ?? Infinity)) continue;

      cost[next] = through;
      cameFrom[next] = current;
      open.push(next, through + octile(nx, nz, goal.tx, goal.tz));
    }
  }

  if (bestIndex === startIndex) return EMPTY_PATH;

  const tiles: TilePos[] = [];
  for (let index = bestIndex; index !== startIndex && index >= 0; index = cameFrom[index] ?? -1) {
    tiles.push({
      tx: originX + (index % SEARCH_SIZE),
      tz: originZ + Math.floor(index / SEARCH_SIZE),
      plane,
    });
  }
  tiles.reverse();

  return { tiles, points: tiles.map((tile) => centerOf(tile)), exact: found };
}

/** Length of a diagonal step, against one for a cardinal step. */
const DIAGONAL = Math.SQRT2;

/**
 * Shortest possible distance between two tiles when diagonals are allowed and cost
 * what they are worth. Never overestimates, which is what makes the search find the
 * shortest route rather than merely a short one.
 */
function octile(ax: number, az: number, bx: number, bz: number): number {
  const dx = Math.abs(ax - bx);
  const dz = Math.abs(az - bz);
  return dx + dz - (2 - DIAGONAL) * Math.min(dx, dz);
}

/**
 * A binary heap of tile indices ordered by their estimated total cost.
 *
 * Written out rather than sorting an array, because this runs for every click of
 * every player and a sort over a few thousand entries is the kind of thing that
 * only shows itself as a stutter once a shard is busy.
 */
class Heap {
  private readonly items: Int32Array;
  private readonly scores: Float32Array;
  private size = 0;

  constructor(capacity: number) {
    this.items = new Int32Array(capacity);
    this.scores = new Float32Array(capacity);
  }

  get empty(): boolean {
    return this.size === 0;
  }

  push(item: number, score: number): void {
    if (this.size >= this.items.length) return;

    let index = this.size++;
    this.items[index] = item;
    this.scores[index] = score;

    while (index > 0) {
      const parent = (index - 1) >> 1;
      if ((this.scores[parent] ?? Infinity) <= (this.scores[index] ?? Infinity)) break;
      this.swap(parent, index);
      index = parent;
    }
  }

  pop(): number {
    if (this.size === 0) return -1;

    const top = this.items[0] ?? -1;
    this.size--;

    if (this.size > 0) {
      this.items[0] = this.items[this.size] ?? 0;
      this.scores[0] = this.scores[this.size] ?? 0;

      let index = 0;
      for (;;) {
        const left = index * 2 + 1;
        const right = left + 1;
        let smallest = index;

        if (
          left < this.size &&
          (this.scores[left] ?? Infinity) < (this.scores[smallest] ?? Infinity)
        ) {
          smallest = left;
        }
        if (
          right < this.size &&
          (this.scores[right] ?? Infinity) < (this.scores[smallest] ?? Infinity)
        ) {
          smallest = right;
        }
        if (smallest === index) break;

        this.swap(smallest, index);
        index = smallest;
      }
    }

    return top;
  }

  private swap(a: number, b: number): void {
    const item = this.items[a] ?? 0;
    const score = this.scores[a] ?? 0;
    this.items[a] = this.items[b] ?? 0;
    this.scores[a] = this.scores[b] ?? 0;
    this.items[b] = item;
    this.scores[b] = score;
  }
}

/**
 * The free tile beside a target that is closest to where the walker is standing.
 *
 * Both sides need the same answer: the client walks there so the walk is predicted
 * like any other movement, and the server walks there so it agrees. Deriving it
 * twice from different code is how the two end up a tile apart, and a tile apart is
 * the difference between reaching something and standing next to it doing nothing.
 */
export function tileBeside(
  map: CollisionMap,
  target: TilePos,
  from: TilePos,
  /**
   * Whether to stand square to it rather than at a corner.
   *
   * True for anything reached over an edge rather than across one: casting a
   * line into the water from the corner of the bank is not how anybody fishes.
   * A corner is still taken when none of the four sides can be reached, because
   * standing on a corner beats not getting there at all.
   */
  square = false,
): TilePos | null {
  const sides = square ? DIRECTIONS.filter((dir) => dir.dx === 0 || dir.dz === 0) : DIRECTIONS;

  return (
    nearestOf(map, target, from, sides) ??
    (square ? nearestOf(map, target, from, DIRECTIONS) : null)
  );
}

/** Whichever of a set of neighbours is nearest to somebody, or none of them. */
function nearestOf(
  map: CollisionMap,
  target: TilePos,
  from: TilePos,
  dirs: readonly { dx: number; dz: number }[],
): TilePos | null {
  let best: TilePos | null = null;
  let bestDistance = Infinity;
  let bestCorner = true;

  for (const dir of dirs) {
    const tx = target.tx + dir.dx;
    const tz = target.tz + dir.dz;
    if (map.isBlocked(tx, tz, target.plane)) continue;

    const distance = Math.max(Math.abs(tx - from.tx), Math.abs(tz - from.tz));
    const corner = dir.dx !== 0 && dir.dz !== 0;

    // Nearest wins, and a side wins a tie. Without that last part the corners
    // take everything: they come first in the list of directions, and walking
    // to a corner costs exactly what walking to the side beside it costs, so a
    // character asked to stand next to something stood on its corner every
    // time.
    const better = distance < bestDistance || (distance === bestDistance && bestCorner && !corner);
    if (!better) continue;

    bestDistance = distance;
    bestCorner = corner;
    best = { tx, tz, plane: target.plane };
  }

  return best;
}

/** Whether two tiles are close enough to interact, which is touching or overlapping. */
export function withinReach(a: TilePos, b: TilePos): boolean {
  return withinRange(a, b, 1);
}

/**
 * The same question at any distance, for anything thrown, shot or cast.
 *
 * Measured the way every other distance in the game is: diagonally adjacent is
 * one tile away, not one and a half.
 */
export function withinRange(a: TilePos, b: TilePos, range: number): boolean {
  if (a.plane !== b.plane) return false;
  return Math.max(Math.abs(a.tx - b.tx), Math.abs(a.tz - b.tz)) <= Math.max(1, range);
}
